import { type DocumentValueType } from "~/graphql/supergraphTypes";
import { formatShortFormDate } from "~/utils/datetime";
import {
  CHAPTER_3_STATUS_OPTIONS,
  CHAPTER_4_STATUS_OPTIONS,
  LOB_TYPES_OPTIONS,
  type TaxFormType,
  type TaxDocumentCategory,
  getTaxFormTypeFromDocument,
  isFatcaFormType,
} from "../../../TaxForms";
import {
  CRS_CONTROLLING_PERSON_TYPE_OPTIONS,
  CRS_FATCA_STATUS_OPTIONS,
  CRS_STATUS_OPTIONS,
} from "../../../TaxForms/configs/crsEntity.config";
import { US_STATUS_OPTIONS } from "../../../TaxForms/configs/crsIndividual.config";
import { FATCA_PARTS } from "../../../TaxForms/configs/w8bene.config";
import { W8ECI_ENTITY_TYPE_OPTIONS } from "../../../TaxForms/configs/w8eci.config";
import {
  W8EXP_CHAPTER_4_STATUS_OPTIONS,
  W8EXP_ORG_TYPE_OPTIONS,
} from "../../../TaxForms/configs/w8exp.config";
import {
  W8IMY_CHAPTER_3_STATUS_OPTIONS,
  W8IMY_CHAPTER_4_STATUS_OPTIONS,
  W8IMY_US_TIN_TYPE_OPTIONS,
} from "../../../TaxForms/configs/w8imy.config";
import {
  EXEMPT_PAYEE_CODE_OPTIONS,
  FATCA_EXEMPTION_CODE_OPTIONS,
  LLC_TAX_CLASSIFICATION_OPTIONS,
  TAX_CLASSIFICATION_OPTIONS,
} from "../../../TaxForms/configs/w9.config";
import { type SelectOption } from "../../../TaxForms/types";
import { selfCertificationDocumentTypesArray } from "../DocumentDetailsFormValues/SelfCertificationFormValues/constants";
import { wSeriesTaxFormTypesArray } from "../DocumentDetailsFormValues/WSeriesTaxDocFormValues/constants";

// Re-export the shared function for backward compatibility
export { getTaxFormTypeFromDocument };

const EMPTY_DISPLAY_VALUE = { hasValue: false, displayValue: "" } as const;

export type SourceDocumentAssignment = {
  investorName: string;
  entityName?: string;
  positionId?: string;
  arenaId?: number;
};

export type TaxDetailItem = {
  key: string;
  label: string;
  hasValue: boolean;
  displayValue: string;
  isSectionHeader?: boolean;
  isLastInGroup?: boolean;
  showCheckmark?: boolean;
  /** Whether this item (e.g., signature date) represents an expired document */
  isExpired?: boolean;
  /** Source document assignments for the source document status field */
  sourceDocumentAssignments?: SourceDocumentAssignment[];
  isSecondaryValue?: boolean;
  linkedValue?: string;
};

/**
 * Gets the human-readable label for a document subtype (e.g., "W-8BEN", "Individual Self-Certification").
 */
export const getDocumentSubtypeLabel = (
  category: TaxDocumentCategory,
  secondaryDocumentType: string | undefined,
): string => {
  if (!secondaryDocumentType) return "";

  if (category === "wSeries") {
    const formType = wSeriesTaxFormTypesArray.find((t) => t.value === secondaryDocumentType);
    return formType?.readableName ?? secondaryDocumentType;
  }
  if (category === "selfCertification") {
    const formType = selfCertificationDocumentTypesArray.find(
      (t) => t.value === secondaryDocumentType,
    );
    return formType?.readableName ?? secondaryDocumentType;
  }
  return secondaryDocumentType;
};

type BuildTaxFormUrlParams = {
  taxFormType: TaxFormType;
  documentId: number | undefined;
  currentSearch: string;
  currentUrl: string;
  profileId?: string;
};

/**
 * Builds the URL for navigating to the tax form page.
 */
export const buildTaxFormUrl = ({
  taxFormType,
  documentId,
  currentSearch,
  currentUrl,
  profileId,
}: BuildTaxFormUrlParams): string => {
  const searchParams = new URLSearchParams(currentSearch);
  searchParams.set("formType", taxFormType);

  if (documentId) {
    searchParams.set("documentId", String(documentId));
  }

  if (profileId) {
    searchParams.set("profileId", profileId);
  }

  const basePath = currentUrl.replace(/\/document-management.*/, "/document-management");
  return `${basePath}/tax-form?${searchParams.toString()}`;
};

const getMetadataValue = (
  metadata: DocumentValueType[] | null,
  key: string,
): string | undefined => {
  return metadata?.find((m) => m?.key === key)?.value ?? undefined;
};

const getLabelFromOptions = (value: string | undefined, options: SelectOption[]): string => {
  if (!value) return "";
  const option = options.find((opt) => opt.value === value);
  return option?.label ?? value;
};

type FieldType = "radio" | "jsonArray" | "jsonObject" | "boolean" | "date" | "country";

type TaxDetailConfig = {
  key: string;
  metadataKey: string;
  label: string;
  fieldType?: FieldType;
  options?: SelectOption[];
  trueLabel?: string;
  /** If true, this field is only shown in the profile details view, not in the document sidebar */
  profileOnly?: boolean;
  /** When the primary value is empty, check this metadata key (boolean) and show fallbackLabel */
  fallbackMetadataKey?: string;
  fallbackLabel?: string;
  /** For boolean fields: show a linked metadata value below the checkmark text */
  linkedMetadataKey?: string;
  linkedFieldType?: FieldType;
  /** For jsonArray (tax residencies): override the jurisdiction field label */
  jurisdictionLabel?: string;
};

type TaxDetailSectionConfig = {
  title?: string;
  fields: TaxDetailConfig[];
};

export type ProfileFieldsData = {
  signatureDate?: string | null;
  documentName?: string;
  sourceDocumentAssignments?: SourceDocumentAssignment[];
  isExpired?: boolean;
};

type GetTaxDetailsOptions = {
  /** Include fields marked as profileOnly. Default: false */
  includeProfileFields?: boolean;
  /** Additional data for profile-only fields */
  profileFieldsData?: ProfileFieldsData;
};

const TAX_FORM_DETAIL_CONFIGS: Record<TaxFormType, TaxDetailSectionConfig[]> = {
  WITHHOLDING_STATEMENT: [
    {
      fields: [
        {
          key: "entityName",
          metadataKey: "entity_name",
          label: "Entity name",
          profileOnly: true,
        },
      ],
    },
  ],
  W9: [
    {
      fields: [
        { key: "name", metadataKey: "name", label: "Name" },
        {
          key: "disregardedEntityName",
          metadataKey: "disregarded_entity_name",
          label: "Disregarded entity name",
        },
        {
          key: "taxClassification",
          metadataKey: "tax_classification",
          label: "Tax classification",
          options: TAX_CLASSIFICATION_OPTIONS,
        },
        {
          key: "llcClassification",
          metadataKey: "llc_classification",
          label: "LLC tax classification",
          options: LLC_TAX_CLASSIFICATION_OPTIONS,
        },
        {
          key: "exemptPayeeCode",
          metadataKey: "exempt_payee_code",
          label: "Exempt payee code",
          options: EXEMPT_PAYEE_CODE_OPTIONS,
        },
        {
          key: "fatcaExemptionCode",
          metadataKey: "exemption_from_fatca_reporting_code",
          label: "FATCA exemption code",
          options: FATCA_EXEMPTION_CODE_OPTIONS,
        },
        { key: "address", metadataKey: "address", label: "Address", fieldType: "jsonObject" },
      ],
    },
    {
      fields: [{ key: "taxId", metadataKey: "tax_id", label: "Tax ID" }],
    },
  ],
  W8BEN: [
    {
      fields: [
        { key: "name", metadataKey: "name", label: "Name" },
        {
          key: "countryOfCitizenship",
          metadataKey: "country_of_citizenship",
          label: "Country of citizenship",
          fieldType: "country",
        },
        {
          key: "permanentResidenceAddress",
          metadataKey: "permanent_residence_address",
          label: "Permanent residence address",
          fieldType: "jsonObject",
        },
        { key: "usTin", metadataKey: "us_tin", label: "US TIN" },
        {
          key: "foreignTin",
          metadataKey: "foreign_tin",
          label: "Foreign TIN",
          fallbackMetadataKey: "ftin_not_legally_required",
          fallbackLabel: "FTIN not legally required",
        },
        {
          key: "dateOfBirth",
          metadataKey: "date_of_birth",
          label: "Date of birth",
          fieldType: "date",
        },
      ],
    },
    {
      fields: [
        {
          key: "treatyCountry",
          metadataKey: "treaty_country",
          label: "Treaty country",
          fieldType: "country",
        },
        {
          key: "treatyRateClaimed",
          metadataKey: "treaty_rate_claimed",
          label: "Treaty rate claimed",
        },
        { key: "treatyIncomeType", metadataKey: "treaty_income_type", label: "Income type" },
      ],
    },
  ],
  W8BENE: [
    {
      fields: [
        { key: "entityName", metadataKey: "name", label: "Entity name" },
        {
          key: "countryOfIncorporation",
          metadataKey: "country_of_incorporation",
          label: "Country of incorporation",
          fieldType: "country",
        },
        {
          key: "disregardedEntityName",
          metadataKey: "disregarded_entity_name",
          label: "Disregarded entity name",
        },
        {
          key: "chapter3Status",
          metadataKey: "chapter_3_status",
          label: "Chapter 3 status",
          options: CHAPTER_3_STATUS_OPTIONS,
        },
        {
          key: "chapter4Status",
          metadataKey: "chapter_4_status",
          label: "Chapter 4 status (FATCA)",
          options: CHAPTER_4_STATUS_OPTIONS,
        },
        {
          key: "permanentResidenceAddress",
          metadataKey: "permanent_residence_address",
          label: "Permanent residence address",
          fieldType: "jsonObject",
        },
        { key: "usTin", metadataKey: "us_tin", label: "US TIN" },
        { key: "giin", metadataKey: "giin", label: "GIIN" },
        {
          key: "giinVerified",
          metadataKey: "giin_verified",
          label: "GIIN verification",
          fieldType: "boolean",
          trueLabel: "Verified through the IRS portal",
        },
        { key: "foreignTin", metadataKey: "foreign_tin", label: "Foreign TIN" },
      ],
    },
    {
      fields: [{ key: "sponsorName", metadataKey: "sponsor_name", label: "Sponsor name" }],
    },
    {
      fields: [
        {
          key: "isTreatyCountryResident",
          metadataKey: "is_treaty_country_resident",
          label: "Treaty claim",
          fieldType: "boolean",
          trueLabel:
            "The beneficial owner is a resident of the following country within the meaning of the income tax treaty between the United States and that country:",
          linkedMetadataKey: "treaty_country",
          linkedFieldType: "country",
        },
        {
          key: "hasLimitationOnBenefits",
          metadataKey: "has_limitation_on_benefits",
          label: "",
          fieldType: "boolean",
          trueLabel:
            "The beneficial owner derives the item (or items) of income for which the treaty benefits are claimed, and, if applicable, meets the requirements of the treaty provision dealing with limitation on benefits. The following are types of limitation on benefits provisions that may be included in an applicable tax treaty:",
          linkedMetadataKey: "limitation_on_benefits_type",
          options: LOB_TYPES_OPTIONS,
        },
        {
          key: "isQualifiedResidentForDividends",
          metadataKey: "is_qualified_resident_for_dividends",
          label: "",
          fieldType: "boolean",
          trueLabel:
            "The beneficial owner is claiming treaty benefits for U.S. source dividends received from a foreign corporation or interest from a U.S. trade or business of a foreign corporation and meets qualified resident status",
        },
        {
          key: "treatyRateClaimed",
          metadataKey: "treaty_rate_claimed",
          label: "Treaty rate claimed",
        },
        { key: "treatyIncomeType", metadataKey: "treaty_income_type", label: "Income type" },
      ],
    },
  ],
  W8ECI: [
    {
      fields: [
        { key: "entityName", metadataKey: "entity_name", label: "Entity name" },
        {
          key: "countryOfIncorporation",
          metadataKey: "country_of_incorporation",
          label: "Country of incorporation",
          fieldType: "country",
        },
        {
          key: "entityType",
          metadataKey: "entity_type",
          label: "Entity type",
          options: W8ECI_ENTITY_TYPE_OPTIONS,
        },
        {
          key: "foreignAddress",
          metadataKey: "foreign_address",
          label: "Foreign address",
          fieldType: "jsonObject",
        },
        {
          key: "usBusinessAddress",
          metadataKey: "us_business_address",
          label: "US business address",
          fieldType: "jsonObject",
        },
        { key: "usTin", metadataKey: "us_tin", label: "US TIN" },
        {
          key: "incomeDescription",
          metadataKey: "income_description",
          label: "Income description",
        },
        {
          key: "dealerPtpCheckbox",
          metadataKey: "dealer_ptp_checkbox",
          label: "Check to certify as Dealer/PTP",
          fieldType: "boolean",
          trueLabel: "Dealer/PTP",
        },
      ],
    },
  ],
  W8EXP: [
    {
      fields: [
        { key: "entityName", metadataKey: "entity_name", label: "Entity name" },
        {
          key: "countryOfIncorporation",
          metadataKey: "country_of_incorporation",
          label: "Country of incorporation",
          fieldType: "country",
        },
        {
          key: "orgType",
          metadataKey: "org_type",
          label: "Org type",
          options: W8EXP_ORG_TYPE_OPTIONS,
        },
        {
          key: "chapter4Status",
          metadataKey: "chapter_4_status",
          label: "Chapter 4 status",
          options: W8EXP_CHAPTER_4_STATUS_OPTIONS,
        },
        {
          key: "permanentResidenceAddress",
          metadataKey: "permanent_residence_address",
          label: "Permanent residence address",
          fieldType: "jsonObject",
        },
        { key: "usTin", metadataKey: "us_tin", label: "US TIN" },
        { key: "giin", metadataKey: "giin", label: "GIIN" },
        {
          key: "giinVerified",
          metadataKey: "giin_verified",
          label: "GIIN verified",
          fieldType: "boolean",
          trueLabel: "Verified through the IRS portal",
        },
        { key: "foreignTin", metadataKey: "foreign_tin", label: "Foreign TIN" },
      ],
    },
  ],
  W8IMY: [
    {
      fields: [
        { key: "entityName", metadataKey: "entity_name", label: "Entity name" },
        {
          key: "countryOfIncorporation",
          metadataKey: "country_of_incorporation",
          label: "Country of incorporation",
          fieldType: "country",
        },
        {
          key: "disregardedEntityName",
          metadataKey: "disregarded_entity_name",
          label: "Disregarded entity name",
        },
        {
          key: "chapter3Status",
          metadataKey: "chapter_3_status",
          label: "Chapter 3 status",
          options: W8IMY_CHAPTER_3_STATUS_OPTIONS,
        },
        {
          key: "chapter4Status",
          metadataKey: "chapter_4_status",
          label: "Chapter 4 status",
          options: W8IMY_CHAPTER_4_STATUS_OPTIONS,
        },
        {
          key: "permanentResidenceAddress",
          metadataKey: "permanent_residence_address",
          label: "Permanent residence address",
          fieldType: "jsonObject",
        },
        {
          key: "usTinType",
          metadataKey: "us_tin_type",
          label: "US TIN type",
          options: W8IMY_US_TIN_TYPE_OPTIONS,
        },
        { key: "usTin", metadataKey: "us_tin", label: "US TIN" },
        { key: "giin", metadataKey: "giin", label: "GIIN" },
        {
          key: "giinVerified",
          metadataKey: "giin_verified",
          label: "GIIN verified",
          fieldType: "boolean",
          trueLabel: "Verified through the IRS portal",
        },
        { key: "foreignTin", metadataKey: "foreign_tin", label: "Foreign TIN" },
      ],
    },
    {
      fields: [{ key: "sponsorName", metadataKey: "sponsor_name", label: "Sponsor name" }],
    },
  ],
  CRS_INDIVIDUAL: [
    {
      fields: [
        { key: "name", metadataKey: "name", label: "Name" },
        {
          key: "dateOfBirth",
          metadataKey: "date_of_birth",
          label: "Date of birth",
          fieldType: "date",
        },
        { key: "cityOfBirth", metadataKey: "city_of_birth", label: "City of birth" },
        {
          key: "countryOfBirth",
          metadataKey: "country_of_birth",
          label: "Country of birth",
          fieldType: "country",
        },
        {
          key: "permanentResidenceAddress",
          metadataKey: "permanent_residence_address",
          label: "Permanent residence address",
          fieldType: "jsonObject",
        },
      ],
    },
    {
      fields: [
        {
          key: "usStatusDeclaration",
          metadataKey: "us_status_declaration",
          label: "US status declaration",
          fieldType: "radio",
          options: US_STATUS_OPTIONS,
          linkedMetadataKey: "us_tin",
        },
      ],
    },
    {
      fields: [
        {
          key: "taxResidencies",
          metadataKey: "tax_residencies",
          label: "Tax residencies",
          fieldType: "jsonArray",
          jurisdictionLabel: "Country of tax residency",
        },
      ],
    },
  ],
  CRS_ENTITY: [
    {
      fields: [
        { key: "entityName", metadataKey: "entity_name", label: "Entity name" },
        {
          key: "countryOfIncorporation",
          metadataKey: "country_of_incorporation",
          label: "Country of incorporation",
          fieldType: "country",
        },
        {
          key: "permanentResidenceAddress",
          metadataKey: "permanent_residence_address",
          label: "Permanent residence address",
          fieldType: "jsonObject",
        },
      ],
    },
    {
      fields: [
        {
          key: "fatcaStatus",
          metadataKey: "fatca_status",
          label: "FATCA status",
          options: CRS_FATCA_STATUS_OPTIONS,
        },
        {
          key: "crsStatus",
          metadataKey: "crs_status",
          label: "CRS status",
          options: CRS_STATUS_OPTIONS,
        },
        { key: "giin", metadataKey: "giin", label: "GIIN" },
        { key: "sponsorName", metadataKey: "sponsor_name", label: "Sponsor name" },
        { key: "sponsorGiin", metadataKey: "sponsor_giin", label: "Sponsor GIIN" },
        {
          key: "giinVerified",
          metadataKey: "giin_verified",
          label: "GIIN and Sponsor GIIN verification",
          fieldType: "boolean",
          trueLabel: "Verified through the IRS portal",
        },
      ],
    },
    {
      fields: [
        {
          key: "taxResidencies",
          metadataKey: "tax_residencies",
          label: "Tax residencies",
          fieldType: "jsonArray",
        },
      ],
    },
  ],
};

/**
 * Formats a JSON address object into a readable multi-line string.
 */
const getAddressDisplayValue = (
  rawValue: string | undefined,
  countryCodeToName?: Record<string, string>,
): { hasValue: boolean; displayValue: string } => {
  if (!rawValue) return EMPTY_DISPLAY_VALUE;
  try {
    const parsed = JSON.parse(rawValue) as Record<string, string>;
    if (typeof parsed !== "object" || parsed === null) {
      return EMPTY_DISPLAY_VALUE;
    }
    const street = [parsed.street, parsed.street2].filter(Boolean).join(", ");
    const cityStateZip = [parsed.city, parsed.state, parsed.postal_code]
      .filter(Boolean)
      .join(", ");
    const countryCode = parsed.country;
    const country =
      countryCode && countryCodeToName
        ? (countryCodeToName[countryCode] ?? countryCode)
        : countryCode;
    const lines = [street, cityStateZip, country].filter(Boolean);
    if (lines.length === 0) return EMPTY_DISPLAY_VALUE;
    return { hasValue: true, displayValue: lines.join("\n") };
  } catch {
    return EMPTY_DISPLAY_VALUE;
  }
};

type BackendTaxResidency = {
  jurisdiction?: string;
  has_tax_reference_number?: boolean;
  tax_reference_number_type?: string;
  tax_reference_number?: string;
  reason_for_no_tin?: string;
};

type BackendControllingPerson = {
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  address?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  } | null;
  tin?: string;
  controlling_person_type?: string;
};

const expandTaxResidencies = (
  rawValue: string | undefined,
  countryCodeToName?: Record<string, string>,
  jurisdictionLabel = "Jurisdiction of tax residency",
): TaxDetailItem[] => {
  if (!rawValue) return [];
  try {
    const parsed = JSON.parse(rawValue) as BackendTaxResidency[];
    if (!Array.isArray(parsed) || parsed.length === 0) return [];

    return parsed.flatMap((entry, index) => {
      const headerLabel = parsed.length === 1 ? "Tax residency" : `Tax residency ${index + 1}`;
      const jurisdiction = entry.jurisdiction
        ? (countryCodeToName?.[entry.jurisdiction] ?? entry.jurisdiction)
        : "";
      const items: TaxDetailItem[] = [
        {
          key: `taxResHeader_${index}`,
          label: headerLabel,
          hasValue: true,
          displayValue: "",
          isSectionHeader: true,
        },
        {
          key: `taxRes_${index}_jurisdiction`,
          label: jurisdictionLabel,
          hasValue: !!jurisdiction,
          displayValue: jurisdiction,
        },
      ];
      if (entry.has_tax_reference_number === false && entry.reason_for_no_tin) {
        items.push({
          key: `taxRes_${index}_noTin`,
          label: "No TIN?",
          hasValue: true,
          displayValue: entry.reason_for_no_tin,
        });
      } else {
        items.push(
          {
            key: `taxRes_${index}_tinType`,
            label: "Tax reference number type",
            hasValue: !!entry.tax_reference_number_type,
            displayValue: entry.tax_reference_number_type ?? "",
          },
          {
            key: `taxRes_${index}_tin`,
            label: "Tax reference number",
            hasValue: !!entry.tax_reference_number,
            displayValue: entry.tax_reference_number ?? "",
          },
        );
      }
      const isLastEntry = index === parsed.length - 1;
      if (!isLastEntry) {
        items[items.length - 1].isLastInGroup = true;
      }
      return items;
    });
  } catch {
    return [];
  }
};

const formatControllingPersonAddress = (
  address: BackendControllingPerson["address"],
  countryCodeToName?: Record<string, string>,
): string => {
  if (!address) return "";
  const parts: string[] = [];
  if (address.street) parts.push(address.street);
  if (address.street2) parts.push(address.street2);
  const cityStateZip = [address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(", ");
  if (cityStateZip) parts.push(cityStateZip);
  if (address.country) {
    const countryName = countryCodeToName?.[address.country] ?? address.country;
    parts.push(countryName);
  }
  return parts.join("\n");
};

const formatControllingPersonName = (entry: BackendControllingPerson): string => {
  return [entry.first_name, entry.middle_name, entry.last_name].filter(Boolean).join(" ");
};

type ControllingPersonType = "fatca_substantial_owner" | "crs_controlling_person";

const isBackendControllingPerson = (
  metaData: Record<string, unknown>,
): metaData is BackendControllingPerson => {
  if (typeof metaData !== "object" || metaData === null) return false;
  // Validate expected structure - at least one name field should be present or be a valid object
  const hasNameField =
    "first_name" in metaData || "last_name" in metaData || "middle_name" in metaData;
  const hasAddress = "address" in metaData;
  const hasTin = "tin" in metaData;
  return hasNameField || hasAddress || hasTin;
};

/**
 * Expands tax reporting data items (from taxReportingData query) into display items.
 */
const expandControllingPersonsFromTaxReportingData = (
  taxReportingItems: { id: number; metaData: Record<string, unknown> }[],
  type: ControllingPersonType,
  countryCodeToName?: Record<string, string>,
): TaxDetailItem[] => {
  if (taxReportingItems.length === 0) return [];

  const headerBase =
    type === "fatca_substantial_owner" ? "FATCA Substantial owner" : "CRS Controlling persons";

  return taxReportingItems.flatMap((item, index) => {
    if (!isBackendControllingPerson(item.metaData)) return [];
    const meta = item.metaData;
    const headerLabel =
      taxReportingItems.length === 1 ? headerBase : `${headerBase} ${index + 1}`;
    const name = formatControllingPersonName(meta);
    const address = formatControllingPersonAddress(meta.address, countryCodeToName);

    const items: TaxDetailItem[] = [
      {
        key: `${type}_header_${item.id}`,
        label: headerLabel,
        hasValue: true,
        displayValue: "",
        isSectionHeader: true,
      },
      {
        key: `${type}_${item.id}_name`,
        label: "Name",
        hasValue: !!name,
        displayValue: name,
      },
    ];

    if (type === "crs_controlling_person" && meta.controlling_person_type) {
      const typeLabel = getLabelFromOptions(
        meta.controlling_person_type,
        CRS_CONTROLLING_PERSON_TYPE_OPTIONS,
      );
      items.push({
        key: `${type}_${item.id}_type`,
        label: "CRS controlling persons type",
        hasValue: true,
        displayValue: typeLabel,
      });
    }

    items.push(
      {
        key: `${type}_${item.id}_address`,
        label: "Address",
        hasValue: !!address,
        displayValue: address,
      },
      {
        key: `${type}_${item.id}_tin`,
        label: "TIN",
        hasValue: !!meta.tin,
        displayValue: meta.tin ?? "",
      },
    );

    const isLastEntry = index === taxReportingItems.length - 1;
    if (!isLastEntry) {
      items[items.length - 1].isLastInGroup = true;
    }
    return items;
  });
};

/**
 * Gets the display value for a boolean field.
 */
const getBooleanDisplayValue = (
  rawValue: string | undefined,
  trueLabel?: string,
): { hasValue: boolean; displayValue: string; showCheckmark: boolean } => {
  if (rawValue === undefined || rawValue === "") {
    return { ...EMPTY_DISPLAY_VALUE, showCheckmark: false };
  }
  const isTrue = rawValue === "true" || rawValue === "1" || rawValue === "yes";
  return {
    hasValue: true,
    displayValue: isTrue ? (trueLabel ?? "Yes") : "No",
    showCheckmark: isTrue,
  };
};

/**
 * Processes a single field config and returns its display item(s).
 */
const processField = (
  field: TaxDetailConfig,
  metadata: DocumentValueType[] | null,
  countryCodeToName?: Record<string, string>,
): TaxDetailItem[] => {
  const rawValue = getMetadataValue(metadata, field.metadataKey);

  if (field.fieldType === "jsonArray") {
    const expandedItems = expandTaxResidencies(
      rawValue,
      countryCodeToName,
      field.jurisdictionLabel,
    );
    if (expandedItems.length > 0) return expandedItems;
    return [{ key: field.key, label: field.label, ...EMPTY_DISPLAY_VALUE }];
  }

  if (field.fieldType === "jsonObject") {
    const { hasValue, displayValue } = getAddressDisplayValue(rawValue, countryCodeToName);
    return [{ key: field.key, label: field.label, hasValue, displayValue }];
  }

  if (field.fieldType === "boolean") {
    const { hasValue, displayValue, showCheckmark } = getBooleanDisplayValue(
      rawValue,
      field.trueLabel,
    );
    if (!showCheckmark && !field.label) {
      return [];
    }
    let linkedValue: string | undefined;
    if (hasValue && field.linkedMetadataKey) {
      const linkedRaw = getMetadataValue(metadata, field.linkedMetadataKey);
      if (linkedRaw) {
        if (field.linkedFieldType === "country" && countryCodeToName) {
          linkedValue = countryCodeToName[linkedRaw] ?? linkedRaw;
        } else if (field.options) {
          linkedValue = getLabelFromOptions(linkedRaw, field.options);
        } else {
          linkedValue = linkedRaw;
        }
      }
    }
    return [
      {
        key: field.key,
        label: field.label,
        hasValue,
        displayValue,
        showCheckmark,
        linkedValue,
      },
    ];
  }

  if (field.fieldType === "date" && rawValue) {
    let displayValue = "";
    try {
      displayValue = formatShortFormDate(rawValue);
    } catch {
      // rawValue is not a valid ISO date; render as empty
    }
    return [{ key: field.key, label: field.label, hasValue: !!displayValue, displayValue }];
  }

  if (field.fieldType === "country" && rawValue && countryCodeToName) {
    const displayValue = countryCodeToName[rawValue] ?? rawValue;
    return [{ key: field.key, label: field.label, hasValue: true, displayValue }];
  }

  const hasValue = rawValue !== undefined && rawValue !== "";
  if (!hasValue && field.fallbackMetadataKey && field.fallbackLabel) {
    const fallbackValue = getMetadataValue(metadata, field.fallbackMetadataKey);
    if (fallbackValue === "true") {
      return [
        {
          key: field.key,
          label: field.label,
          hasValue: true,
          displayValue: field.fallbackLabel,
          isSecondaryValue: true,
        },
      ];
    }
  }
  let displayValue = field.options
    ? getLabelFromOptions(rawValue, field.options)
    : (rawValue ?? "");

  if (field.metadataKey === "treaty_rate_claimed" && hasValue && displayValue) {
    displayValue = `${displayValue}%`;
  }

  const showCheckmark = field.fieldType === "radio" && hasValue;
  let linkedValue: string | undefined;
  if (showCheckmark && field.linkedMetadataKey) {
    const linkedRaw = getMetadataValue(metadata, field.linkedMetadataKey);
    if (linkedRaw) {
      linkedValue = linkedRaw;
    }
  }
  return [
    { key: field.key, label: field.label, hasValue, displayValue, showCheckmark, linkedValue },
  ];
};

const getFatcaPartItem = (metadata: DocumentValueType[] | null): TaxDetailItem | null => {
  const chapter4Status = getMetadataValue(metadata, "chapter_4_status");
  if (!chapter4Status) return null;

  const fatcaPart = FATCA_PARTS.find((part) => part.statusValue === chapter4Status);
  if (!fatcaPart) return null;

  const statusLabel = getLabelFromOptions(chapter4Status, CHAPTER_4_STATUS_OPTIONS);

  return {
    key: "fatcaPartConfirmation",
    label: statusLabel,
    hasValue: true,
    displayValue: `Required Part ${fatcaPart.partNumber} was completed`,
    showCheckmark: true,
  };
};

type TaxReportingDataItem = {
  id: number;
  taxReportingType: "fatca_substantial_owner" | "crs_controlling_person";
  metaData: Record<string, unknown>;
};

export const getTaxDetailsFromMetadata = (
  metadata: DocumentValueType[] | null,
  formType: TaxFormType,
  countryCodeToName?: Record<string, string>,
  taxReportingData?: TaxReportingDataItem[],
  options?: GetTaxDetailsOptions,
): TaxDetailItem[] => {
  const { includeProfileFields = false, profileFieldsData } = options ?? {};
  const sections = TAX_FORM_DETAIL_CONFIGS[formType];
  if (!sections) return [];

  const items = sections.flatMap((section, sectionIndex) => {
    const sectionItems: TaxDetailItem[] = [];
    const isLastSection = sectionIndex === sections.length - 1;

    if (section.title) {
      sectionItems.push({
        key: `sectionHeader_${sectionIndex}`,
        label: section.title,
        hasValue: true,
        displayValue: "",
        isSectionHeader: true,
      });
    }

    // Filter out profileOnly fields when not in profile view
    const fieldsToProcess = section.fields.filter(
      (field) => includeProfileFields || !field.profileOnly,
    );

    const fieldItems = fieldsToProcess.flatMap((field) =>
      processField(field, metadata, countryCodeToName),
    );

    if (!isLastSection && fieldItems.length > 0) {
      fieldItems[fieldItems.length - 1].isLastInGroup = true;
    }

    sectionItems.push(...fieldItems);
    return sectionItems;
  });

  // Helper to append items with proper group separation
  const appendItemsWithGroupSeparator = (newItems: TaxDetailItem[]) => {
    if (newItems.length > 0 && items.length > 0) {
      items[items.length - 1].isLastInGroup = true;
    }
    items.push(...newItems);
  };

  if (formType === "W8BENE") {
    const fatcaPartItem = getFatcaPartItem(metadata);
    if (fatcaPartItem) {
      appendItemsWithGroupSeparator([fatcaPartItem]);
    }

    // Add FATCA Substantial Owners from taxReportingData
    const fatcaOwners = taxReportingData?.filter(
      (item) => item.taxReportingType === "fatca_substantial_owner",
    );
    if (fatcaOwners && fatcaOwners.length > 0) {
      const ownerItems = expandControllingPersonsFromTaxReportingData(
        fatcaOwners,
        "fatca_substantial_owner",
        countryCodeToName,
      );
      appendItemsWithGroupSeparator(ownerItems);
    }
  }

  if (formType === "CRS_ENTITY") {
    const crsControllers = taxReportingData?.filter(
      (item) => item.taxReportingType === "crs_controlling_person",
    );
    if (crsControllers && crsControllers.length > 0) {
      const controllerItems = expandControllingPersonsFromTaxReportingData(
        crsControllers,
        "crs_controlling_person",
        countryCodeToName,
      );
      appendItemsWithGroupSeparator(controllerItems);
    } else {
      appendItemsWithGroupSeparator([
        {
          key: "crsControllingPersonsHeader",
          label: "CRS Controlling persons",
          hasValue: true,
          displayValue: "",
          isSectionHeader: true,
        },
        {
          key: "crsControllingPersonsEmpty",
          label: "No controlling persons",
          hasValue: true,
          displayValue: "",
        },
      ]);
    }
  }

  if (includeProfileFields && profileFieldsData) {
    const { signatureDate, documentName, sourceDocumentAssignments, isExpired } =
      profileFieldsData;
    const isFatca = isFatcaFormType(formType);

    if (signatureDate) {
      const signatureDateItem: TaxDetailItem = {
        key: "signatureDate",
        label: "Signature date",
        hasValue: true,
        displayValue: signatureDate,
        isExpired,
      };
      appendItemsWithGroupSeparator([signatureDateItem]);
    }

    const documentInfoItems: TaxDetailItem[] = [];

    if (documentName) {
      documentInfoItems.push({
        key: "documentName",
        label: "Document name",
        hasValue: true,
        displayValue: documentName,
      });
    }

    if (sourceDocumentAssignments && sourceDocumentAssignments.length > 0) {
      const sourceLabel = isFatca
        ? "FATCA source document status"
        : "CRS source document status";
      const createdPositions = sourceDocumentAssignments.filter(
        (assignment) => assignment.positionId !== undefined && assignment.positionId !== null,
      );

      // Only show the status if there are created positions
      if (createdPositions.length > 0) {
        documentInfoItems.push({
          key: "sourceDocumentStatus",
          label: sourceLabel,
          hasValue: true,
          displayValue: `${createdPositions.length} ${createdPositions.length === 1 ? "position" : "positions"}`,
          sourceDocumentAssignments: createdPositions,
        });
      }
    }

    if (documentInfoItems.length > 0) {
      appendItemsWithGroupSeparator(documentInfoItems);
    }
  }

  return items;
};
